/**
 * Date utility functions to handle date formatting without timezone conversion issues
 */

/**
 * Format a date string (YYYY-MM-DD) to a readable format without timezone conversion
 * @param dateString - Date string in YYYY-MM-DD format
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string
 */
export function formatDateLocal(dateString: string, options?: Intl.DateTimeFormatOptions): string {
  if (!dateString) return ''
  
  // Parse the date string manually to avoid timezone conversion
  const [year, month, day] = dateString.split('-').map(Number)
  const date = new Date(year, month - 1, day) // month is 0-indexed
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }
  
  return date.toLocaleDateString('en-US', options || defaultOptions)
}

/**
 * Format a date range without timezone conversion
 * @param startDate - Start date string in YYYY-MM-DD format
 * @param endDate - End date string in YYYY-MM-DD format (optional)
 * @returns Formatted date range string
 */
export function formatDateRange(startDate: string, endDate?: string): string {
  if (!startDate) return ''
  
  if (!endDate || startDate === endDate) {
    return formatDateLocal(startDate)
  }
  
  const startFormatted = formatDateLocal(startDate, { month: 'short', day: 'numeric' })
  const endFormatted = formatDateLocal(endDate, { month: 'short', day: 'numeric' })
  
  return `${startFormatted} - ${endFormatted}`
}

/**
 * Get an array of date strings between start and end dates
 * @param startDate - Start date string in YYYY-MM-DD format
 * @param endDate - End date string in YYYY-MM-DD format (optional)
 * @returns Array of date strings in YYYY-MM-DD format
 */
export function getDaysArray(startDate: string, endDate?: string): string[] {
  if (!startDate) return []
  
  const [startYear, startMonth, startDay] = startDate.split('-').map(Number)
  const start = new Date(startYear, startMonth - 1, startDay)
  
  let end = start
  if (endDate) {
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number)
    end = new Date(endYear, endMonth - 1, endDay)
  }
  
  const days: string[] = []
  const current = new Date(start)
  
  while (current <= end) {
    const year = current.getFullYear()
    const month = String(current.getMonth() + 1).padStart(2, '0')
    const day = String(current.getDate()).padStart(2, '0')
    days.push(`${year}-${month}-${day}`)
    current.setDate(current.getDate() + 1)
  }
  
  return days
}

/**
 * Format time string for display
 * @param timeString - Time string in HH:MM format
 * @returns Formatted time string
 */
export function formatTime(timeString: string): string {
  if (!timeString) return ''
  
  try {
    const [hours, minutes] = timeString.split(':').map(Number)
    const date = new Date()
    date.setHours(hours, minutes, 0, 0)
    
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  } catch (error) {
    return timeString
  }
}
